/**
 * PWA manifest + app icon wiring.
 *
 * This project has no editable index.html, so the tags that normally live in a
 * static <head> are inserted here at runtime instead, once, on boot. Browsers
 * read link[rel=manifest], link[rel=apple-touch-icon], link[rel=icon] and
 * meta[name=theme-color] from the live DOM, so an install prompt and a proper
 * home-screen icon both work from injected tags.
 *
 * The Cloudflare Worker in public/cloudflare-worker.js ALSO injects the same
 * tags into the served HTML, which is what makes them visible to crawlers and
 * to the iOS "Add to Home Screen" sheet (Safari reads the icon before the app
 * has hydrated). Keep the two in sync — the constants below are the reference
 * values.
 */

// Finished 512×512 app tile: the real 206Gravel mark in white on the brand
// green, padded so Android's maskable circle/squircle crop can't clip it.
// PNG rather than SVG because iOS ignores SVG for apple-touch-icon.
export const APP_ICON_SRC =
  'https://cdn.magicpatterns.com/uploads/wknXby56U81uMWqoAuY2md/icon512.png'

export const MANIFEST_PATH = '/manifest.webmanifest'

// Brand green — used for the manifest background, the Android status bar, and
// the iOS standalone status bar.
export const THEME_COLOR = '#006328'

function upsertLink(
  rel: string,
  href: string,
  attrs: Record<string, string> = {},
) {
  const selectorParts = [`link[rel="${rel}"]`]
  if (attrs.sizes) selectorParts.push(`[sizes="${attrs.sizes}"]`)
  const selector = selectorParts.join('')

  let el = document.head.querySelector<HTMLLinkElement>(selector)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  el.href = href
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value)
  }
}

function upsertMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/**
 * Insert the manifest link, favicon, Apple touch icon, and theme colour.
 * Idempotent — safe to call more than once.
 */
export function initializeAppIcons() {
  if (typeof document === 'undefined') return

  upsertLink('manifest', MANIFEST_PATH)

  // NOTE: no rel="icon" here on purpose. The browser-tab favicon is set in the
  // Publish panel and is tuned for 16px; overwriting it with the 512px app
  // tile would make the tab read as a green blob.

  // iOS home screen. Safari ignores the manifest icons entirely and reads
  // apple-touch-icon, so this tag is what actually produces the app icon on
  // an iPhone.
  upsertLink('apple-touch-icon', APP_ICON_SRC, { sizes: '180x180' })

  upsertMeta('theme-color', THEME_COLOR)
  upsertMeta('apple-mobile-web-app-capable', 'yes')
  upsertMeta('apple-mobile-web-app-status-bar-style', 'black-translucent')
  upsertMeta('apple-mobile-web-app-title', '206Gravel')
  upsertMeta('mobile-web-app-capable', 'yes')
  upsertMeta('application-name', '206Gravel')
}
